import { computed } from 'vue';
import { useUISettings } from './useUISettings';
import { useAccount } from './useAccount';

/**
 * BonBoZ: same priority as selectTranslation, but returns the locale KEY.
 *
 * The third branch is why this matters. When neither the agent's nor the
 * account's locale has been translated yet, the picker falls back to whatever
 * translation happens to exist first — so a Vietnamese agent can be shown an
 * English translation. Rendering that without naming the language is worse than
 * showing nothing: it reads as "this is your language" when it is not.
 *
 * @param {Object} translations - Translations object with locale keys
 * @param {string} agentLocale - Agent's preferred locale
 * @param {string} accountLocale - Account's default locale
 * @returns {string|null} Locale key of the selected translation
 */
export function selectTranslationLocale(
  translations,
  agentLocale,
  accountLocale,
  requestedLocale
) {
  if (!translations) return null;
  const keys = Object.keys(translations);
  if (keys.length === 0) return null;

  // BonBoZ: ban dich nguoi truc VUA yeu cau (server ghi vao
  // `bonboz_dich_hien`) thang moi uu tien khac — thieu no thi dich sang en
  // xong van bi ban vi (trung locale giao dien) de len, nhin nhu khong dich.
  if (requestedLocale && translations[requestedLocale]) return requestedLocale;
  if (agentLocale && translations[agentLocale]) return agentLocale;
  if (accountLocale && translations[accountLocale]) return accountLocale;
  return keys[0];
}

/**
 * Select translation based on locale priority.
 * @param {Object} translations - Translations object with locale keys
 * @param {string} agentLocale - Agent's preferred locale
 * @param {string} accountLocale - Account's default locale
 * @returns {string|null} Selected translation or null
 */
export function selectTranslation(
  translations,
  agentLocale,
  accountLocale,
  requestedLocale
) {
  return (
    translations?.[
      selectTranslationLocale(
        translations,
        agentLocale,
        accountLocale,
        requestedLocale
      )
    ] ?? null
  );
}

/**
 * Composable to extract translation state/content from contentAttributes.
 * @param {Ref|Reactive} contentAttributes - Ref or reactive object containing `translations` property
 * @returns {Object} { hasTranslations, translationContent }
 */
export function useTranslations(contentAttributes) {
  const { uiSettings } = useUISettings();
  const { currentAccount } = useAccount();

  const hasTranslations = computed(() => {
    if (!contentAttributes.value) return false;
    const { translations = {} } = contentAttributes.value;
    return Object.keys(translations || {}).length > 0;
  });

  const translationContent = computed(() => {
    if (!hasTranslations.value) return null;
    return selectTranslation(
      contentAttributes.value.translations,
      uiSettings.value?.locale,
      currentAccount.value?.locale,
      contentAttributes.value.bonbozDichHien
    );
  });

  const translationLocale = computed(() => {
    if (!hasTranslations.value) return null;
    return selectTranslationLocale(
      contentAttributes.value.translations,
      uiSettings.value?.locale,
      currentAccount.value?.locale,
      contentAttributes.value.bonbozDichHien
    );
  });

  return { hasTranslations, translationContent, translationLocale };
}
